import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as FileSaver from 'file-saver';
import {SkyObject} from '../aladin-sky-objects.service';
import {Footprint, SourceNameSearchRadius} from 'asax-common';
import {SkyLayer} from "../aladin-layers/aladin-sky-layers-options/aladin-sky-layers-options.component";
import {VOServiceOverlay} from "../aladin-vo/aladin-vo.component";

declare const A: any;

export interface AladinMouseMove {
  readonly ra: number;
  readonly dec: number;
  readonly x: number;
  readonly y: number;
}

function generatePolygon(fov: number, fovCorners: number[][]): string {
  if (fov < 100 && fovCorners) {
    const fovCornersString = [].concat.apply([], fovCorners.reverse()).filter(p => !!p).join(' ');
    return `POLYGON J2000 ${fovCornersString}`;
  }

  return 'Circle ICRS 0 -90 180';
}

@Component({
  selector: 'asax-sky-view-aladin-sky',
  templateUrl: './aladin-sky.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AladinSkyComponent implements OnInit, OnChanges {
  @ViewChild('aladin', {static: true}) aladinElement: ElementRef;

  @Input() height: number;
  @Input() width: number;

  @Input() raDecFov: [number, number, number];

  @Input() baseImage: SkyLayer;
  @Input() overlayImage: SkyLayer;
  @Input() alpha: number;

  @Input() footprints: any[];
  @Input() skyObjects: any[];
  @Input() cells: any[];

  @Input() sourceNameSearchRadius: SourceNameSearchRadius;

  @Input() raDecSearchRadius: SourceNameSearchRadius;

  @Input() footprintsRowHovered: any[];

  @Input() addVoServiceOverlay: VOServiceOverlay;
  @Input() removeVoServiceOverlay: string;

  @Output() outAladinSearchPolygon = new EventEmitter<string>();
  @Output() outAladinFov = new EventEmitter<number>();
  @Output() outAladinMouseMove = new EventEmitter<AladinMouseMove>();
  @Output() outContextmenu = new EventEmitter<MouseEvent>();
  @Output() outMouseClick = new EventEmitter();

  @Output() outAladinSkyObjectHovered = new EventEmitter<SkyObject>();
  @Output() outAladinFootprintClicked = new EventEmitter<Footprint>();
  @Output() outAladinFootprintHovered = new EventEmitter<Footprint>();
  @Output() outAladinSearchRadiusHovered = new EventEmitter<Footprint>();

  private aladin: any;
  private mocs: any[];

  private crossIcon: HTMLImageElement;

  private overlays: any[];
  private overlayFootprints: any;
  private overlayFootprintsHover: any;
  private overlayFootprintsSelected: any;
  private overlayFootprintsHoverSelected: any;
  private overlaySearchRadius: any;

  private catalogSkyObject: any;
  private catalogSkyObjectHover: any;
  private catalogSkyObjectBestMatch: any;
  private catalogSkyObjectBestMatchHover: any;
  private catalogSearchRadius: any;

  constructor(private readonly zone: NgZone) {
  }

  ngOnInit(): void {
    this.initAladin();
    this.addListeners();
    this.addFootprintsOverlays();
    this.addSkyObjectCatalogs();
  }

  private initAladin(): void {
    this.aladin = A.aladin(this.aladinElement.nativeElement, {
      showGotoControl: false,
      showZoomControl: false,
      showFullscreenControl: false,
      reticleColor: '#195775',
      showLayersControl: false,
      showReticle: false,
      fov: 180
    });

    this.addCrossIconToAladin();
  }

  private addCrossIconToAladin(): void {
    this.crossIcon = new Image();
    this.crossIcon.src = 'assets/images/cross.png';
    this.catalogSearchRadius = A.catalog({shape: this.crossIcon});
    this.aladin.addCatalog(this.catalogSearchRadius);
  }

  private addFootprintsOverlays(): void {
    this.zone.runOutsideAngular(() => {
      this.overlayFootprints = this.aladin.createOverlay({color: '#ffe233', lineWidth: 4});
      this.aladin.addOverlay(this.overlayFootprints);

      this.overlayFootprintsHover = this.aladin.createOverlay({color: '#ffff00', lineWidth: 6});
      this.aladin.addOverlay(this.overlayFootprintsHover);

      this.overlayFootprintsSelected = this.aladin.createOverlay({color: '#ff8c1a', lineWidth: 6});
      this.aladin.addOverlay(this.overlayFootprintsSelected);

      this.overlayFootprintsHoverSelected = this.aladin.createOverlay({color: '#cd6600', lineWidth: 6});
      this.aladin.addOverlay(this.overlayFootprintsHoverSelected);

      this.overlaySearchRadius = this.aladin.createOverlay({dashed: true, color: '#0073a8', lineWidth: 4});
      this.aladin.addOverlay(this.overlaySearchRadius);

      this.overlays = [
        this.overlayFootprints,
        this.overlayFootprintsHover,
        this.overlayFootprintsSelected,
        this.overlayFootprintsHoverSelected,
        this.overlaySearchRadius
      ];
    });
  }

  private addSkyObjectCatalogs(): void {
    this.catalogSkyObject = A.catalog({sourceSize: 12, color: '#194775'});
    this.aladin.addCatalog(this.catalogSkyObject);

    this.catalogSkyObjectHover = A.catalog({sourceSize: 12, color: '#0073a8'});
    this.aladin.addCatalog(this.catalogSkyObjectHover);

    this.catalogSkyObjectBestMatch = A.catalog({sourceSize: 12, color: '#ffe233'});
    this.aladin.addCatalog(this.catalogSkyObjectBestMatch);

    this.catalogSkyObjectBestMatchHover = A.catalog({sourceSize: 12, color: '#ffff00'});
    this.aladin.addCatalog(this.catalogSkyObjectBestMatchHover);
  }

  private addListeners(): void {
    this.aladin.on('positionChanged', () => this.onPositionOrZoomChanged());
    this.aladin.on('zoomChanged', () => this.onPositionOrZoomChanged());

    this.aladin.on('objectHovered', (object: any) => this.onObjectHovered(object));
    this.aladin.on('objectClicked', (object: any) => this.onObjectClicked(object));

    this.aladin.on('mouseMove', (mouseMove: AladinMouseMove) => this.onMouseMoveListener(mouseMove));

    this.aladin.on('click', (mouseClick) => this.outMouseClick.emit(mouseClick));
  }

  private onPositionOrZoomChanged(): void {
    const fov = Math.max(...this.aladin.getFov());
    const fovCorners = this.aladin.getFovCorners(4);
    const polygon = generatePolygon(fov, fovCorners);

    this.outAladinSearchPolygon.emit(polygon);
    this.outAladinFov.emit(fov);
  }

  private onObjectHovered(object: any): void {
    switch (object?.objectType) {
      case 'footprint':
        this.onFootprintHovered(object);
        return;
      case 'skyObject':
        this.onSkyObjectHovered(object);
        return;
      case 'searchRadius':
        this.onAladinSearchRadiusHovered(object);
        return;
      default:
        this.onFootprintHovered(undefined);
        this.onSkyObjectHovered(undefined);
        this.onAladinSearchRadiusHovered(undefined);
    }
  }

  private onObjectClicked(object: any): void {
    switch (object?.objectType) {
      case 'footprint':
        this.onFootprintClicked(object);
        return;
    }
  }

  private onSkyObjectHovered(skyObject: SkyObject): void {
    this.outAladinSkyObjectHovered.emit(skyObject);
  }

  private onAladinSearchRadiusHovered(footprint: Footprint): void {
    this.outAladinSearchRadiusHovered.emit(footprint);
  }

  private onFootprintHovered(footprint: Footprint): void {
    this.removeFootprintsFromOverlay(this.overlayFootprintsHover);
    this.removeFootprintsFromOverlay(this.overlayFootprintsHoverSelected);

    if (footprint?.selected) {
      this.addFootprintToOverlay(footprint, this.overlayFootprintsHoverSelected);
    } else if (footprint) {
      this.addFootprintToOverlay(footprint, this.overlayFootprintsHover);
    }

    this.outAladinFootprintHovered.emit(footprint);
  }

  private onFootprintClicked(footprint: Footprint): void {
    this.outAladinFootprintClicked.emit({...footprint, selected: !footprint.selected});
  }

  private onMouseMoveListener(mouseMove: AladinMouseMove): void {
    this.outAladinMouseMove.emit(mouseMove);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.height || changes.width) {
      this.zone.runOutsideAngular(() => setTimeout(() => this.aladin?.view?.fixLayoutDimensions()));
    }
    if (changes.raDecFov) {
      this.onRaDecFov();
    }
    if (changes.baseImage) {
      this.onBaseImage();
    }
    if (changes.overlayImage) {
      this.onOverlayImage();
    }
    if (changes.alpha) {
      this.onAlpha();
    }
    if (changes.footprints) {
      this.onFootprints();
    }
    if (changes.skyObjects) {
      this.onSkyObjects();
    }
    if (changes.cells) {
      this.onCells();
    }
    if (changes.sourceNameSearchRadius) {
      this.onSearchRadius();
    }
    if (changes.raDecSearchRadius) {
      this.onSearchRadius();
    }
    if (changes.footprintsRowHovered) {
      this.onFootprintsRowHovered();
    }
    if (changes.addVoServiceOverlay) {
      this.onAddVoServiceOverlay();
    }
    if (changes.removeVoServiceOverlay) {
      this.onRemoveVoServiceOverlay();
    }
  }

  private onBaseImage(): void {
    this.zone.runOutsideAngular(() => {
      const baseImageHips = this.createImageSurvey(this.baseImage);
      this.aladin?.setBaseImageLayer(baseImageHips);
      this.aladin?.getBaseImageLayer()?.getColorMap()?.update(this.baseImage?.color?.label);
    });
  }

  private onOverlayImage(): void {
    this.zone.runOutsideAngular(() => {
      const overlayImageHips = this.createImageSurvey(this.overlayImage);
      this.aladin?.setOverlayImageLayer(overlayImageHips);
      this.aladin?.getOverlayImageLayer()?.getColorMap().update(this.overlayImage?.color?.label);
    });
  }

  private onAlpha(): void {
    this.zone.runOutsideAngular(() => this.aladin?.getOverlayImageLayer()?.setAlpha(this.alpha));
  }

  private createImageSurvey(image: SkyLayer): any {
    const imageSurvey = [image?.id, image?.id, image?.baseUrl, image?.hipsFrame, image?.hipsOrder, {imgFormat: image?.format}];
    return this.aladin?.createImageSurvey(...imageSurvey);
  }

  private onFootprints(): void {
    const footprintsSelected = this.footprints?.filter(footprint => footprint.selected);
    const footprintsNonSelected = this.footprints?.filter(footprint => !footprint.selected);
    const footprintsSelectedHover = this.footprints?.filter(footprint => footprint.selected)
      ?.filter(footprint => this.footprintsRowHovered?.some(({datasetId}) => datasetId === footprint.datasetId)
        || this.overlayFootprintsHover?.overlays?.some(({datasetId}) => datasetId === footprint.datasetId)
      );

    this.removeFootprintsFromOverlay(this.overlayFootprints);
    this.removeFootprintsFromOverlay(this.overlayFootprintsSelected);
    this.removeFootprintsFromOverlay(this.overlayFootprintsHoverSelected);

    this.addFootprintsToOverlay(footprintsNonSelected, this.overlayFootprints);
    this.addFootprintsToOverlay(footprintsSelected, this.overlayFootprintsSelected);
    this.addFootprintsToOverlay(footprintsSelectedHover, this.overlayFootprintsHoverSelected);
  }

  private onFootprintsRowHovered(): void {
    const footprintsSelected = this.footprintsRowHovered?.filter(footprint => footprint.selected);
    const footprintsNonSelected = this.footprintsRowHovered?.filter(footprint => !footprint.selected);

    this.removeFootprintsFromOverlay(this.overlayFootprintsHover);
    this.removeFootprintsFromOverlay(this.overlayFootprintsHoverSelected);

    this.addFootprintsToOverlay(footprintsNonSelected, this.overlayFootprintsHover);
    this.addFootprintsToOverlay(footprintsSelected, this.overlayFootprintsHoverSelected);
  }

  private addFootprintToOverlay(footprint: Footprint, overlay: any): void {
    this.addFootprintsToOverlay([footprint], overlay);
  }

  private addFootprintsToOverlay(footprints: Footprint[], overlay: any): void {
    this.zone.runOutsideAngular(() => {
      footprints?.filter(footprint => !!footprint).forEach(footprint => {
        const footprintsSTCS = this.aladin.createFootprintsFromSTCS(footprint?.footprint);
        footprintsSTCS?.forEach(footprintSTCS => {
          footprintSTCS.selected = footprint.selected;
          footprintSTCS.footprint = footprint.footprint;
          footprintSTCS.datasetId = footprint.datasetId;
          footprintSTCS.objectType = 'footprint';
        });
        overlay?.addFootprints(footprintsSTCS);
      });
      overlay?.reportChange();
    });
  }

  private removeFootprintsFromOverlay(overlay: any): void {
    this.zone.runOutsideAngular(() => {
      overlay?.removeAll();
      overlay?.reportChange();
    });
  }

  private onSkyObjects(): void {
    this.removeSkyObjectsFromCatalogs();
    this.addSkyObjectsToCatalogs();
  }

  private removeSkyObjectsFromCatalogs(): void {
    this.zone.runOutsideAngular(() => {
      this.catalogSkyObject?.removeAll();
      this.catalogSkyObjectHover?.removeAll();
      this.catalogSkyObjectBestMatch?.removeAll();
      this.catalogSkyObjectBestMatchHover?.removeAll();
    });
  }

  private addSkyObjectsToCatalogs(): void {
    this.zone.runOutsideAngular(() => {
      this.skyObjects?.forEach(skyObject => {
        if (skyObject.isBestMatch) {
          this.catalogSkyObjectBestMatch.addSources([this.buildObjectTypeMarkerFromSkyObject(skyObject)]);
        } else {
          this.catalogSkyObject.addSources([this.buildObjectTypeMarkerFromSkyObject(skyObject)]);
        }
      });
    });
  }

  private buildObjectTypeMarkerFromSkyObject(skyObject: any): any {
    const {ra, dec, isBestMatch, id} = skyObject;

    const marker = A.marker(ra, dec);
    marker.isBestMatch = isBestMatch;
    marker.id = id;
    marker.objectType = 'skyObject';

    return marker;
  }

  private onCells(): void {
    this.removeCellsFromOverlays();
    this.addCellsToOverlays();
  }

  private addCellsToOverlays(): void {
    this.zone.runOutsideAngular(() => {
      const maxTotal = this.cells?.[0]?.total;
      this.cells?.forEach(cell => {
        const opacity = calculateMocOpacity(cell?.total, maxTotal);
        const moc = A.MOCFromJSON({[cell.mocOrder]: [cell.key]}, {opacity, color: '#ffcc33'});

        this.mocs?.push(moc);
        this.aladin?.addMOC(moc);

        if (!this.displayCells) {
          moc?.hide();
        }
      });

      this.aladin?.view?.allOverlayLayers?.filter(overlayLayer => overlayLayer.type !== 'overlay').forEach(overlayLayer => overlayLayer.reportChange());
    });
  }

  private removeCellsFromOverlays(): void {
    this.zone.runOutsideAngular(() => {
      if (this.aladin) {
        this.aladin.view.allOverlayLayers = this.aladin.view.allOverlayLayers.filter(o => o.type === 'overlay');
        this.aladin.view.mocs = [];
        this.mocs = [];
      }
    });
  }

  private onSearchRadius(): void {
    this.zone.runOutsideAngular(() => {
      this.overlaySearchRadius?.removeAll();
      this.catalogSearchRadius?.removeAll();

      if (this.sourceNameSearchRadius || this.raDecSearchRadius) {
        const {ra, dec, radius} = this.sourceNameSearchRadius ?? this.raDecSearchRadius;

        if (ra && dec && radius) {
          const circle = A.circle(ra, dec, radius / 60);
          circle.objectType = 'searchRadius';
          circle.radius = radius;
          this.overlaySearchRadius.add(circle);
        }
      }
    });
  }

  zoomIn(): void {
    this.zone.runOutsideAngular(() => this.aladin.increaseZoom());
  }

  zoomOut(): void {
    this.zone.runOutsideAngular(() => this.aladin.decreaseZoom());
  }

  snapshot(): void {
    const height = this.aladinElement.nativeElement.offsetHeight;
    const width = this.aladinElement.nativeElement.offsetWidth;
    const options = {format: 'image/jpeg', height, width};
    const viewDataURL = this.aladin.getViewDataURL(options);
    const blob = dataURItoBlob(viewDataURL);
    (FileSaver as any).saveAs(blob, `aladin.jpeg`);
  }

  displayCells(displayCells: boolean): void {
    this.zone.runOutsideAngular(() => {
      if (displayCells) {
        this.mocs.forEach(moc => moc.show());
      } else {
        this.mocs.forEach(moc => moc.hide());
      }
    });
  }

  displayFootprints(displayFootprints: boolean): void {
    this.zone.runOutsideAngular(() => {
      if (displayFootprints) {
        this.overlays.forEach(overlay => overlay.show());
      } else {
        this.overlays.forEach(overlay => overlay.hide());
      }
    });
  }

  displaySkyObjects(displaySkyObjects: boolean): void {
    this.zone.runOutsideAngular(() => {
      if (displaySkyObjects) {
        this.catalogSkyObject.show();
        this.catalogSkyObjectBestMatch.show();
      } else {
        this.catalogSkyObject.hide();
        this.catalogSkyObjectBestMatch.hide();
      }
    });
  }

  private onRaDecFov(): void {
    this.zone.runOutsideAngular(() => {
      const width = this.aladinElement?.nativeElement?.offsetWidth || this.width;
      const height = this.aladinElement?.nativeElement?.offsetHeight || this.height;
      const ratio = width / height;
      const fov = Math.min(this.raDecFov?.[2] * ratio, 180);

      this.aladin?.setFov(fov);
      this.aladin?.gotoRaDec(this.raDecFov?.[0], this.raDecFov?.[1]);
    });
  }

  onContextmenu(mouseEvent: MouseEvent): void {
    this.outContextmenu.next(mouseEvent);
    mouseEvent.preventDefault();
  }

  onClick(mouseEvent: MouseEvent): void {
    this.outContextmenu.next(undefined);
  }

  private onAddVoServiceOverlay() {
    if (this.addVoServiceOverlay) {
      const {id, color, footprints} = this.addVoServiceOverlay;
      const overlayVoServicesFootprints = this.aladin.createOverlay({name: id, color, lineWidth: 6});
      this.aladin.addOverlay(overlayVoServicesFootprints);
      this.addFootprintsVoService(footprints, overlayVoServicesFootprints);
    }
  }

  private addFootprintsVoService(footprints: string[], overlay: any): void {
    this.zone.runOutsideAngular(() => {
      footprints?.filter(footprint => !!footprint).forEach(footprint => {
        const footprintsSTCS = this.aladin.createFootprintsFromSTCS(footprint);
        overlay?.addFootprints(footprintsSTCS);
      });
      overlay?.reportChange();
    });
  }

  private onRemoveVoServiceOverlay() {
    this.zone.runOutsideAngular(() => {
      if (this.aladin) {
        this.aladin.view.allOverlayLayers = this.aladin.view.allOverlayLayers.filter(o => o.name !== this.removeVoServiceOverlay);
        this.aladin.view.overlays = this.aladin.view.overlays.filter(o => o.name !== this.removeVoServiceOverlay);
        this.aladin.view.requestRedraw();
      }
    });
  }
}

export function calculateMocOpacity(total: number, maxTotal: number): number {
  const percent = total / maxTotal;
  return Math.log10(Math.pow(10, 0.05) + percent * (Math.pow(10, 0.4) - Math.pow(10, 0.05)));
}

export function dataURItoBlob(dataURI): Blob {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uInt8Array = new Uint8Array(arrayBuffer);
  const dataView = new DataView(arrayBuffer);

  [...byteString].forEach((value, index) => uInt8Array[index] = byteString.charCodeAt(index));

  return new Blob([dataView], {type: mimeString});
}
